import {
  Box,
  Card,
  CardContent,
  Tooltip as MuiTooltip,
  PopoverPosition,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "common/locales";
import { MoveFieldMenu } from "features/grassWedge";
import { sortBy } from "lodash";
import {
  Field,
  getDemandFromArray,
  useCurrentFarmStockClasses,
  useField,
} from "model";
import { useAnimalGroup } from "model/useAnimalGroupQueries";
import React, { useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const BELLFLOWER_COLOR = "#D3C9E7";

// Single green color for all bars
const BAR_COLOR = "#7EC254";

// Get bar color (single color for all bars)
function getBarColor(): string {
  return BAR_COLOR;
}

// Darken a hex color by a percentage
function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
  const B = Math.max(0, (num & 0x0000ff) - amt);
  return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
}

export default function GrassWedge({
  fields,
  preGrazingTarget,
  postGrazingTarget,
}: GrassWedgeProps) {
  const [menuAnchorPosition, setMenuAnchorPosition] =
    useState<PopoverPosition>();
  const [menuFieldId, setMenuFieldId] = useState<string>("");
  const [isLineHovered, setIsLineHovered] = useState(false);
  const data = prepareData();
  const { t } = useTranslation();

  return (
    <>
      <ChartCard>
        <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
          {/* Legend - positioned top right */}
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: 1,
              padding: "6px 10px",
              border: "1px solid #F0F0F0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: BAR_COLOR,
                  borderRadius: "3px",
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.7rem" }}
              >
                {t("grassWedge.legend.dryMatter")} (kgDM/ha)
              </Typography>
            </Box>
            <MuiTooltip
              title={t("grassWedge.legend.demandLineTooltip")}
              arrow
              placement="left"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  padding: "2px 4px",
                  margin: "-2px -4px",
                  borderRadius: 0.5,
                  backgroundColor: isLineHovered ? "#F1EFF7" : "transparent",
                  transition: "background-color 0.15s ease",
                }}
                onMouseEnter={() => setIsLineHovered(true)}
                onMouseLeave={() => setIsLineHovered(false)}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 3,
                    backgroundColor: "#7D5DA3",
                    borderRadius: 1,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                >
                  {t("grassWedge.legend.demandLine")}
                </Typography>
              </Box>
            </MuiTooltip>
          </Box>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
            >
              <CartesianGrid
                vertical={false}
                stroke="#E0E0E0"
                strokeDasharray="none"
              />
              <XAxis
                type="category"
                interval={0}
                dataKey="name"
                tickCount={data.length}
                tick={(props: CustomAxisTickProps) => (
                  <CustomAxisTick {...props} totalFields={data.length} />
                )}
                axisLine={{ stroke: "#E0E0E0" }}
                tickLine={{ stroke: "#E0E0E0" }}
                height={80}
              />
              <YAxis
                axisLine={{ stroke: "#E0E0E0" }}
                tickLine={{ stroke: "#E0E0E0" }}
                tick={{ fill: "#666", fontSize: 12 }}
              />
              <Tooltip
                content={(props: CustomTooltipProps) => (
                  <CustomTooltip {...props} />
                )}
              />
              <Bar
                dataKey="dryMatterNow"
                fill="#8884d8"
                radius={[4, 4, 0, 0]}
                shape={(props: AreaRectangleProps) => (
                  <AreaRectangle {...props} fields={fields} />
                )}
              >
                {data.map((field: Field) => (
                  <Cell
                    key={field.guid}
                    fill={getBarColor()}
                    onClick={(event) => {
                      setMenuFieldId(field.guid);
                      openMenu(event);
                    }}
                    style={{ cursor: "pointer" }}
                  />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey="demandReference"
                stroke={isLineHovered ? "#5D3D83" : "#7D5DA3"}
                connectNulls
                strokeWidth={isLineHovered ? 5 : 4}
                strokeLinecap="round"
                dot={false}
                onMouseEnter={() => setIsLineHovered(true)}
                onMouseLeave={() => setIsLineHovered(false)}
                style={{
                  cursor: "pointer",
                  transition: "stroke 0.15s ease, stroke-width 0.15s ease",
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </ChartCard>
      <MoveFieldMenu
        anchorPosition={menuAnchorPosition}
        close={closeMenu}
        fieldId={menuFieldId}
      />
    </>
  );

  function prepareData() {
    const sortedFields: Array<Field & { demandReference?: number }> = sortBy(
      fields,
      ({ dryMatterNow }) => -dryMatterNow
    ).map((field) => ({ ...field }));

    // Add demandReference to ALL fields (interpolate between pre and post grazing targets)
    if (sortedFields.length > 0) {
      const totalFields = sortedFields.length;
      sortedFields.forEach((field, index) => {
        if (totalFields === 1) {
          // Single field - use average of pre and post
          field.demandReference = (preGrazingTarget + postGrazingTarget) / 2;
        } else {
          // Interpolate: first field = preGrazingTarget, last field = postGrazingTarget
          const ratio = index / (totalFields - 1);
          field.demandReference =
            preGrazingTarget + (postGrazingTarget - preGrazingTarget) * ratio;
        }
      });
    }

    return sortedFields;
  }

  function openMenu(event: React.MouseEvent<SVGElement, MouseEvent>) {
    setMenuAnchorPosition({ top: event.clientY, left: event.clientX });
  }

  function closeMenu() {
    setMenuAnchorPosition(undefined);
  }
}

type GrassWedgeProps = {
  fields: Field[];
  preGrazingTarget: number;
  postGrazingTarget: number;
};

function AreaRectangle({
  fields,
  width,
  x,
  payload: { area, dryMatterNow },
  fill,
  ...props
}: AreaRectangleProps) {
  const [isHovered, setIsHovered] = useState(false);
  const minArea = Math.min(...fields.map(({ area }) => area));
  const maxArea = Math.max(...fields.map(({ area }) => area));

  const adjustedWidth =
    width * (0.5 + (area - minArea) / (maxArea - minArea) / 2);

  const adjustedX = x + width / 2 - adjustedWidth / 2;

  // Darken the fill color on hover
  const displayFill = isHovered ? darkenColor(fill || BAR_COLOR, 15) : fill;

  return (
    <Rectangle
      x={adjustedX}
      width={adjustedWidth}
      fill={displayFill}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        transition: "fill 0.15s ease",
        filter: isHovered ? "brightness(0.9)" : "none",
      }}
      {...props}
    />
  );
}

type AreaRectangleProps = {
  fields: Field[];
  width: number;
  x: number;
  payload: Field;
  fill?: string;
};

type CustomTooltipProps = {
  payload: Array<{ payload: Field }>;
  active: boolean;
  label: string;
};

function CustomTooltip({ active, label, payload }: CustomTooltipProps) {
  const { t } = useTranslation();

  const fieldId = payload && payload[0] ? payload[0].payload.guid : undefined;
  const { data: field } = useField(fieldId);
  let availableDryMatter;

  const { data: animalGroup } = useAnimalGroup(field?.animalGroup);
  const { data: stockClasses } = useCurrentFarmStockClasses();

  // Filter stock classes for this animal group
  const animalGroupStockClasses = stockClasses.filter(
    (sc) => sc.animalGroup === field?.animalGroup
  );
  const demand = getDemandFromArray(animalGroupStockClasses);

  if (field && animalGroup && animalGroup.postGrazingTarget) {
    availableDryMatter = Math.floor(
      ((field.dryMatterNow - animalGroup.postGrazingTarget) * field.area) /
        10000
    );
    if (availableDryMatter < 0) {
      availableDryMatter = 0;
    }
  }

  if (!payload || !payload[0]) {
    return null;
  }

  let grazingDaysAhead;
  if (availableDryMatter && demand) {
    grazingDaysAhead = availableDryMatter / demand;
  }
  if (active) {
    return (
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
          border: `1px solid ${BELLFLOWER_COLOR}`,
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: "#1a1a2e", mb: 1 }}
          >
            {label}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
            {t("grassWedge.tooltip.currentDM", {
              dm: payload[0].payload.dryMatterNow,
            })}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
            {t("grassWedge.tooltip.area", {
              area: (payload[0].payload.area / 10000).toFixed(2),
            })}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("grassWedge.tooltip.grazindDaysAhead", {
              days: grazingDaysAhead ? Math.floor(grazingDaysAhead) : 0,
              hours: grazingDaysAhead
                ? Math.round((grazingDaysAhead % 1) * 24)
                : 0,
            })}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return null;
}

type CustomAxisTickProps = {
  x: number;
  y: number;
  payload: {
    value: string;
    index: number;
  };
  totalFields: number;
};

function CustomAxisTick({ x, y, payload, totalFields }: CustomAxisTickProps) {
  // Determine max characters based on number of fields
  const maxChars =
    totalFields > 15 ? 8 : totalFields > 10 ? 12 : totalFields < 5 ? 20 : 16;
  const fontSize = totalFields > 15 ? 10 : 11;

  // Don't rotate when fewer than 5 fields
  const shouldRotate = totalFields >= 5;

  // Truncate name if too long
  const displayName =
    payload.value.length > maxChars
      ? `${payload.value.slice(0, maxChars)}…`
      : payload.value;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={shouldRotate ? 12 : 16}
        textAnchor={shouldRotate ? "end" : "middle"}
        fill="#666"
        fontSize={fontSize}
        transform={shouldRotate ? "rotate(-45)" : undefined}
        style={{ fontFamily: "inherit" }}
      >
        {displayName}
      </text>
    </g>
  );
}

const ChartCard = styled("div")(({ theme }) => ({
  width: "100%",
  maxWidth: "900px",
  height: "400px",
  backgroundColor: "white",
  borderRadius: "16px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: "16px",
  boxSizing: "border-box",
  "@media (min-width: 600px)": {
    height: "450px",
  },
}));
